import classNames from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import React, { useEffect, useState } from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { useRemoveReservedAccountNo } from '@apps-common/apis/portal/account-reservation/remove-reserved-account-no'
import { useReserveAccountNo } from '@apps-common/apis/portal/account-reservation/reserve-account-no'
import { ACCOUNT_EXP, ACCOUNT_NO_LENGTH } from '@apps-common/config/account-no'
import { commonIcons } from '@apps-common/config/icons'
import { useGarbageCollectorStore } from '@apps-common/stores'
import { IReserveAccountNoModel } from '@apps-common/types/portal/reserve-account-no'

export enum ReservedAccountNoMessage {
  usedInActive = 'Used in active',
  usedInInactive = 'Used in inactive',
  usedInCancelled = 'Used in cancelled',
  usedInReserved = 'Used in reserved',
  reserved = 'Reserved'
}

export interface IReservedAccountReservation {
  id: number
  accountNo: string
  message: string
  success: boolean
}

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  existingAccountNo?: string
  releaseOnDispose?: () => boolean
  registration: Partial<UseFormRegister<any>>
  containerClassName?: string
  label: string
  showLabel?: boolean
  errors?: FieldError
  info?: string
  loading?: boolean
  disabled?: boolean
  mCentreId?: string
  onError: () => void
  onSuccess: (data: IReserveAccountNoModel) => void
  onReset: () => void
}

const GARBAGE_COLLECTOR_ACCOUNT_RESERVATION_KEY = 'release-reserved-account'

export const AccountReservationInputSM = React.forwardRef<HTMLInputElement, React.PropsWithChildren<InputProps>>(
  (props: InputProps, ref) => {
    const {
      className,
      containerClassName,
      existingAccountNo,
      releaseOnDispose = () => true,
      registration,
      placeholder,
      id,
      showLabel = false,
      label,
      errors,
      info,
      loading = false,
      disabled = false,
      mCentreId,
      onError,
      onSuccess,
      onReset,
      children
    } = props

    const { add, run, remove, purge } = useGarbageCollectorStore()

    const [accountNo, setAccountNo] = useState<string>('')

    const { mutate: removeReserveAccountNo } = useRemoveReservedAccountNo()

    const {
      mutate: reserveAccountNo,
      isPending: reservedAccountNoIsPending,
      data: reserveAccountNoData,
      reset: reserveAccountNoReset
    } = useReserveAccountNo(false, (reserve) => {
      if (reserve?.msg?.toLowerCase() === ReservedAccountNoMessage.reserved.toLowerCase()) {
        onSuccess(reserve)
        add({
          key: GARBAGE_COLLECTOR_ACCOUNT_RESERVATION_KEY,
          callback: () => {
            console.log(`Running remove reserve on ${reserve.id}`)
            removeReserveAccountNo({ id: reserve.id })
          }
        })
      }
      if (reserve?.msg?.toLowerCase() !== ReservedAccountNoMessage.reserved.toLowerCase()) {
        console.log('IS ERROR INSIDE ACCOUNT NO INPUT!!!')
        onError()
      }
    })

    const isSuccess = () => reserveAccountNoData?.msg === ReservedAccountNoMessage.reserved

    const isError = () => reserveAccountNoData && reserveAccountNoData?.msg !== ReservedAccountNoMessage.reserved

    const buildErrorMessage = () => {
      if (accountNo?.length > 0 && !ACCOUNT_EXP.test(accountNo)) {
        return 'Account # should be 4 digits'
      }

      if (reserveAccountNoData?.msg === ReservedAccountNoMessage.reserved) {
        return undefined
      }

      if (
        reserveAccountNoData?.msg === ReservedAccountNoMessage.usedInActive ||
        reserveAccountNoData?.msg === ReservedAccountNoMessage.usedInInactive
      ) {
        return 'Used in active'
      }

      if (reserveAccountNoData?.msg === ReservedAccountNoMessage.usedInCancelled) {
        return 'Used in cancelled'
      }

      if (reserveAccountNoData?.msg === ReservedAccountNoMessage.usedInReserved) {
        return 'Used in reserved'
      }

      return undefined
    }

    useEffect(
      () => () => {
        if (releaseOnDispose()) {
          run(GARBAGE_COLLECTOR_ACCOUNT_RESERVATION_KEY)
        } else {
          remove(GARBAGE_COLLECTOR_ACCOUNT_RESERVATION_KEY)
        }
      },
      []
    )

    useEffect(() => {
      if (mCentreId && mCentreId.length > 0) {
        setAccountNo('')
      }
    }, [mCentreId])

    useEffect(() => {
      if (existingAccountNo && existingAccountNo.length > 0 && existingAccountNo !== accountNo) {
        setAccountNo(existingAccountNo)
      }
    }, [existingAccountNo])

    useEffect(() => {
      if (!ACCOUNT_EXP.test(accountNo)) {
        if (reserveAccountNoData) {
          if (isSuccess() && reserveAccountNoData?.id > 0) {
            removeReserveAccountNo({ id: reserveAccountNoData.id })
          }
          reserveAccountNoReset()
          onReset()
        }
      }

      if (ACCOUNT_EXP.test(accountNo) && mCentreId && !reservedAccountNoIsPending && reserveAccountNoData?.accountNo !== accountNo) {
        reserveAccountNo({ mCentreId, accountNo })
      }
    }, [accountNo, existingAccountNo])

    const errorMessage = buildErrorMessage()

    return (
      <AnimatePresence mode='wait'>
        {loading && (
          <motion.div
            key={`${id}-input-sm-skeleton`}
            initial='initial'
            animate='animate'
            exit='exit'
            variants={variants}
            className={classNames('mb-2 animate-pulse', containerClassName)}
          >
            <div className={classNames('block ml-2 mb-2 h-[16px] bg-skeleton max-w-[20%] rounded-lg', { 'sr-only': !showLabel })} />
            <div className='relative'>
              <div className={classNames('px-4 py-2 rounded-3xl bg-skeleton w-full h-[36px]', className)} />
            </div>
            <p className='pl-2 pt-2 h-[5px]' />
          </motion.div>
        )}
        {!loading && (
          <motion.div
            key={`${id}-input-sm`}
            initial='initial'
            animate='animate'
            exit='exit'
            variants={variants}
            className={classNames('mb-2', containerClassName)}
          >
            <label className={classNames('block pl-2 pb-2 text-sm text-text font-bold', { 'sr-only': !showLabel })} htmlFor={id}>
              {label}
            </label>
            <div className='relative'>
              <input
                id={id}
                type='text'
                placeholder={placeholder}
                className={classNames(
                  'input-bordered bg-surface border !border-border disabled:bg-surface-2 text-text placeholder:text-text-muted/70 input w-full text-sm h-[35px] sm:h-[40px] rounded-[20px] focus:outline-none',
                  (isError() || errorMessage || errors) && 'input-error ring-1 ring-error',
                  className
                )}
                disabled={disabled || !mCentreId || mCentreId?.length === 0 || reservedAccountNoIsPending || isSuccess()}
                value={accountNo}
                onChange={(e) => {
                  const val = e?.target?.value

                  if (val && val?.length <= ACCOUNT_NO_LENGTH) {
                    setAccountNo(val)
                  } else {
                    setAccountNo('')
                  }
                }}
              />
              {isError() && <commonIcons.toastWarning className='absolute text-error text-[1.35rem] top-[8px] right-[10px]' />}
              {isSuccess() && (
                <div className='absolute top-[5px] right-[5px] flex items-center justify-end gap-[5px] bg-surface p-[1px] border border-border rounded-full'>
                  <div className='bg-success text-on-success rounded-full'>
                    <commonIcons.toastSuccess className='text-[1.3rem]' />
                  </div>
                  <button
                    type='button'
                    aria-label='Clear account reservation'
                    className='bg-warning text-on-warning rounded-full'
                    onClick={() => setAccountNo('')}
                  >
                    <commonIcons.dash className='text-[1.35rem]' />
                  </button>
                </div>
              )}

              {children}
            </div>
            <p className='pl-2 pt-2 text-[11px] text-text-muted/70'>{info}</p>
            <p className='pl-2 pt-2 text-[11px] text-error'>{errors?.message}</p>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }
)
